'use client';

import { Button } from '@common/components';
import { useToast } from '@common/hooks';
import SvgChevronright from '@common/icon/SvgChevronright';
import SvgFourLeafClover from '@common/icon/SvgFourLeafClover';
import { cn, CustomError } from '@core/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useEmblaCarousel from 'embla-carousel-react';
import NextImage from 'next/image';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { toast as toastifyToast } from 'react-toastify';
import { useShallow } from 'zustand/react/shallow';

import { AuthMutation, AuthQueryOptions, UnauthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { useBenefitGate } from '@/app/(allowed)/benefit/_hooks/useBenefitGate';
import { Image } from '@/components/client';
import { IMAGE_PLACEHOLDER, ROUTES } from '@/constants';
import { DEFAULT_LOCATION } from '@/constants/geo';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAdGuards } from '@/modules/adpopcorn/_hooks/useAdGuards';
import { useAuthStore } from '@/stores/AuthStore';
import {
  GTM_EVENT_TYPE,
  trackGa4Event,
  trackGtmEvent,
} from '@/utils/analytics/gtm';
import { isBot } from '@/utils/isBot';

type ReadRewardContentType =
  'POLICY' | 'BEST_CONTENTS' | 'HOMETOWN_NEWS' | 'TOUR_FESTIVAL';

const CONTENT_TYPE_META: Record<
  ReadRewardContentType,
  { label: string; toPathname: (idIdx: string) => string }
> = {
  POLICY: {
    label: '정책',
    toPathname: (idIdx) => ROUTES.POLICY_DETAIL.pathname({ idIdx }),
  },
  BEST_CONTENTS: {
    label: '아티클',
    toPathname: (idIdx) => ROUTES.POLICY_INFO_DETAIL.pathname({ idIdx }),
  },
  HOMETOWN_NEWS: {
    label: '동네소식',
    toPathname: (idIdx) => ROUTES.HOMETOWN_NEWS_DETAIL.pathname({ idIdx }),
  },
  TOUR_FESTIVAL: {
    label: '동네축제',
    toPathname: (idIdx) => ROUTES.FESTIVAL_DETAIL.pathname({ idIdx }),
  },
};

const READ_REWARD_SEARCH_PARAM = 'readReward';
const RESUME_SEARCH_VALUE = 'claim';
const NEXT_CONTENT_SEARCH_VALUE = 'next';

const toastLuckyTicket = (message: string) => {
  toastifyToast(message, {
    autoClose: 3_000,
    role: 'status',
    icon: <SvgFourLeafClover className="size-20" originalFill />,
  });
};

const SERVER_MESSAGE_RETURN_CODES = [30400, 30403, 30409];

export const ReadRewardEndMarker = () => (
  <div data-read-reward-end aria-hidden />
);

interface ReadRewardCardProps {
  contentType: ReadRewardContentType;
  contentIdIdx: string;
}

export const ReadRewardCard = ({
  contentType,
  contentIdIdx,
}: ReadRewardCardProps) => {
  const router = useCustomRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { isWelloApp } = useAdGuards();
  const { triggerGate, gateModals } = useBenefitGate();
  const isLogin = useAuthStore((state) => state.isLogin);
  const myInfo = useAuthStore((state) => state.myInfo);
  const [regionCode, subRegionCode] = useAuthStore(
    useShallow(({ region, myInfo }) => [
      region?.at(0)?.codeRegion ||
        myInfo?.codeRegion ||
        DEFAULT_LOCATION.codeRegion,
      region?.at(0)?.codeSubRegion ||
        myInfo?.codeSubRegion ||
        DEFAULT_LOCATION.codeSubRegion,
    ]),
  );
  const hasResumedRef = useRef(false);
  const entrySearchValueRef = useRef<string | null | undefined>(undefined);
  const hasShownEntryToastRef = useRef(false);
  const [isReadComplete, setIsReadComplete] = useState(false);

  const statusQueryOptions = AuthQueryOptions[
    'get:/wello/v2/auth/benefit-read-rewards'
  ]({ query: { contentType, contentIdIdx } });

  const { data: status } = useQuery({
    ...statusQueryOptions,
    enabled: isLogin === true,
    select: (response) => response.context,
  });

  const nextContentsQuery = {
    query: { contentType, contentIdIdx, regionCode, subRegionCode },
  };
  //* 서버가 매 호출 랜덤으로 고르므로 포커스 리페치마다 목록이 뒤섞이지 않게 고정한다.
  const { data: nextContents = [] } = useQuery({
    ...(isLogin
      ? AuthQueryOptions['get:/wello/v2/auth/benefit-read-rewards/contents'](
          nextContentsQuery,
        )
      : UnauthQueryOptions[
          'get:/wello/v2/allows/benefit-read-rewards/contents'
        ](nextContentsQuery)),
    enabled: isLogin !== undefined && !isBot(),
    staleTime: Infinity,
    select: (response) => response.context?.contents ?? [],
  });
  const firstNextContent = nextContents.at(0);

  const { mutate: claim, isPending: isClaiming } = useMutation({
    ...AuthMutation['post:/wello/v2/auth/benefit-read-rewards'](),
    onSuccess: (response) => {
      //* 조회 GET은 읽기 복제본이라 지급 직후엔 늦다 — POST 응답(같은 형태)으로 캐시를 덮는다.
      queryClient.setQueryData(statusQueryOptions.queryKey, response);
      void queryClient.invalidateQueries({
        queryKey:
          AuthQueryOptions['get:/wello/v2/auth/benefit-tickets']().queryKey,
      });

      const context = response.context;
      if (!context?.grantedLuckyCount) {
        return;
      }

      trackGa4Event('reward_grant', {
        category: contentType,
        daily_count: context.dailyGrantedCount,
        acquisition_path: hasResumedRef.current
          ? 'existing_login'
          : 'already_login',
      });

      if (isWelloApp) {
        router.push(ROUTES.BENEFIT_SCRATCH.pathname);

        return;
      }

      toastLuckyTicket('행운권 1장을 받았어요');
    },
    onError: (error) => {
      void queryClient.invalidateQueries({
        queryKey: statusQueryOptions.queryKey,
      });

      if (
        error instanceof CustomError &&
        SERVER_MESSAGE_RETURN_CODES.includes(error.return_code)
      ) {
        toast({ message: error.return_message, type: 'fail' });

        return;
      }

      toast({
        message: '행운권을 받지 못했어요. 다시 시도해 주세요.',
        type: 'fail',
      });
    },
  });

  const claimAfterAdultCheck = () => {
    triggerGate(['adult'], () =>
      claim({ body: { contentType, contentIdIdx } }),
    );
  };

  //* 시안: 이미 받았거나 한도를 다 썼거나 로그인 복귀·「다음 콘텐츠 보기」로 들어온 진입엔 띄우지 않는다.
  useEffect(() => {
    if (entrySearchValueRef.current === undefined) {
      const searchParams = new URLSearchParams(window.location.search);
      entrySearchValueRef.current = searchParams.get(READ_REWARD_SEARCH_PARAM);
      if (entrySearchValueRef.current === NEXT_CONTENT_SEARCH_VALUE) {
        searchParams.delete(READ_REWARD_SEARCH_PARAM);
        const search = searchParams.toString();
        router.replace(`${pathname}${search ? `?${search}` : ''}`, {
          scroll: false,
        });
      }
    }

    if (
      hasShownEntryToastRef.current ||
      entrySearchValueRef.current ||
      !status?.receivableYN
    ) {
      return;
    }

    hasShownEntryToastRef.current = true;
    toastLuckyTicket('끝까지 읽으면 행운권 1장을 드려요!');
  });

  //* 서버엔 "로그인 후 지급 대기"가 없다 — 비로그인 클릭은 복귀 URL에 표식을 남겨 돌아온 뒤 이어서 받는다.
  useEffect(() => {
    if (hasResumedRef.current || isLogin !== true || !myInfo) {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get(READ_REWARD_SEARCH_PARAM) !== RESUME_SEARCH_VALUE) {
      return;
    }

    hasResumedRef.current = true;
    searchParams.delete(READ_REWARD_SEARCH_PARAM);
    const search = searchParams.toString();
    router.replace(`${pathname}${search ? `?${search}` : ''}`, {
      scroll: false,
    });
    claimAfterAdultCheck();
  });

  useEffect(() => {
    const endMarker = document.querySelector('[data-read-reward-end]');
    if (!endMarker) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) {
        return;
      }

      observer.disconnect();
      setIsReadComplete(true);
      trackGa4Event('read_complete', { category: contentType });
    });
    observer.observe(endMarker);

    return () => observer.disconnect();
  }, [contentType, contentIdIdx]);

  const handleClaim = () => {
    trackGtmEvent(GTM_EVENT_TYPE.CLICK_BTN, {
      btn_name: '완독보상',
      content_name: contentType,
      content_id: contentIdIdx,
    });

    if (isLogin !== true) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set(READ_REWARD_SEARCH_PARAM, RESUME_SEARCH_VALUE);
      router.push(
        ROUTES.LOGIN.withSearchParams({
          searchParams: { redirect: `${pathname}?${searchParams.toString()}` },
        }),
      );

      return;
    }

    claimAfterAdultCheck();
  };

  const handleOpen = () => {
    router.push(ROUTES.BENEFIT_SCRATCH.pathname);
  };

  const renderButton = () => {
    if (status && !status.receivableYN) {
      return (
        <div className="flex w-full gap-8">
          <Button
            theme="sub-text"
            dimensions="H40"
            onClick={handleOpen}
            className={cn(
              'bg-Gray-white',
              firstNextContent ? 'shrink-0 whitespace-nowrap px-12' : 'w-full',
            )}
          >
            행운권 열기
            <SvgChevronright className="size-16" />
          </Button>
          {firstNextContent ? (
            <Button
              theme="secondary-fill"
              dimensions="H40"
              asChild
              className="flex-1"
            >
              <CustomLink
                href={`${CONTENT_TYPE_META[
                  firstNextContent.contentType
                ].toPathname(
                  firstNextContent.slugId,
                )}?${READ_REWARD_SEARCH_PARAM}=${NEXT_CONTENT_SEARCH_VALUE}`}
                onClick={() =>
                  trackGtmEvent(GTM_EVENT_TYPE.CLICK_BTN, {
                    btn_name: '완독보상_다음콘텐츠',
                    content_name: firstNextContent.contentType,
                    content_id: firstNextContent.contentIdIdx,
                  })
                }
              >
                다음 콘텐츠 보기
              </CustomLink>
            </Button>
          ) : null}
        </div>
      );
    }

    if (!isReadComplete) {
      return (
        <Button
          theme="secondary-fill"
          dimensions="H40"
          disabled
          className="w-full"
        >
          끝까지 읽으면 행운권 1장 드려요
        </Button>
      );
    }

    return (
      <Button
        theme="secondary-fill"
        dimensions="H40"
        loading={isClaiming}
        onClick={handleClaim}
        className="w-full"
      >
        <SvgFourLeafClover className="size-16" originalFill />
        행운권 받기
      </Button>
    );
  };

  return (
    <section className="w-full px-16 pt-12" aria-label="완독 보상">
      <div className="flex flex-col items-center rounded-large bg-B-25 px-12 pb-12">
        <div className="flex h-64 w-full items-end justify-center gap-4">
          <NextImage
            src="/assets/pngs/read-reward/character.png"
            alt=""
            width={84}
            height={76}
            className="h-76 w-84 shrink-0"
          />
          <p className="py-12 font-700 text-Gray-800 typo-body4">
            여기까지 읽었다면
            <br />
            지금 바로 행운권 드릴게요!
          </p>
        </div>
        <div className="flex w-full flex-col items-center gap-8">
          {renderButton()}
          {status ? (
            <p className="text-Gray-700 typo-caption1">
              오늘 {status.dailyGrantedCount}/{status.dailyCap}장 받음
            </p>
          ) : null}
        </div>
      </div>
      {nextContents.length > 0 ? (
        <NextContentList contents={nextContents} />
      ) : null}
      {gateModals}
    </section>
  );
};

interface NextContent {
  contentType: ReadRewardContentType;
  contentIdIdx: string;
  slugId: string;
  title?: string;
  thumbnailUrl?: string;
}

const NextContentList = ({ contents }: { contents: NextContent[] }) => {
  const [emblaRef] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
  });

  return (
    <div className="flex flex-col gap-12 pt-20">
      <h2 className="font-700 text-Gray-800 typo-body4">콘텐츠 더보기</h2>
      <div
        ref={emblaRef}
        className="-mx-16 cursor-grab overflow-hidden active:cursor-grabbing"
      >
        <ul className="flex gap-12 px-16">
          {contents.map((content) => (
            <li key={content.contentIdIdx} className="w-150 shrink-0">
              <CustomLink
                href={CONTENT_TYPE_META[content.contentType].toPathname(
                  content.slugId,
                )}
                onClick={() =>
                  trackGtmEvent(GTM_EVENT_TYPE.CLICK_BTN, {
                    btn_name: '완독보상_콘텐츠더보기',
                    content_name: content.contentType,
                    content_id: content.contentIdIdx,
                  })
                }
                className="group flex flex-col gap-12 rounded-small focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500"
              >
                <div className="relative size-150 overflow-hidden rounded-small border border-Gray-100 bg-Gray-100">
                  <Image
                    src={content.thumbnailUrl || IMAGE_PLACEHOLDER}
                    alt=""
                    fill
                    sizes="150px"
                    className="object-cover transition-opacity duration-150 group-hover:opacity-80 group-active:opacity-80"
                    unoptimized
                  />
                </div>
                <div className="flex flex-col items-start gap-8">
                  <p className="line-clamp-2 font-700 text-Gray-black typo-body4">
                    {content.title}
                  </p>
                  <span className="rounded-xsmall bg-Gray-200 px-6 py-2 font-600 text-Gray-700 typo-caption1">
                    {CONTENT_TYPE_META[content.contentType].label}
                  </span>
                </div>
              </CustomLink>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
